/* Shoppable video carousel — aidabicaj.com
 * Used by sections/shoppable-video-carousel.liquid
 *
 * - One instance per section, keyed by its root element. Safe to load this
 *   file any number of times (theme editor re-renders re-run it).
 * - Videos load and play only while on screen; they pause off screen.
 * - Clicking a video opens it in a <dialog> popup with sound, its products,
 *   and previous/next. The click is what lets the browser allow sound.
 * - Theme editor: section load/unload and block select are handled.
 */
(function () {
  'use strict';

  if (window.ShoppableVideoCarousel) {
    window.ShoppableVideoCarousel.scan(document);
    return;
  }

  var YT_ORIGIN = 'https://www.youtube-nocookie.com';
  var instances = new Map();
  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  // The shopper's mute choice in the popup, kept for this page view.
  // null = no choice yet, so the section's "Start popup with sound" setting applies.
  var popupMuted = null;

  function noop() {}

  function quietPlay(video) {
    var attempt = video.play();
    if (attempt && attempt.catch) attempt.catch(noop);
    return attempt;
  }

  /* ---------- Picture-in-picture helpers ---------- */

  function inPiP(video) {
    if (!video) return false;
    if (document.pictureInPictureElement === video) return true;
    return video.webkitPresentationMode === 'picture-in-picture';
  }

  function pipSupported(video) {
    if (!video || video.disablePictureInPicture) return false;
    return !!document.pictureInPictureEnabled || typeof video.webkitSetPresentationMode === 'function';
  }

  function requestPiP(video) {
    if (document.pictureInPictureEnabled && video.requestPictureInPicture) {
      video.requestPictureInPicture().catch(function (error) {
        if (window.console) console.warn('[shoppable-video] PiP failed:', error);
      });
    } else if (typeof video.webkitSetPresentationMode === 'function') {
      video.webkitSetPresentationMode('picture-in-picture');
    }
  }

  function exitPiP(video) {
    if (document.pictureInPictureElement === video) {
      document.exitPictureInPicture().catch(noop);
    } else if (video.webkitPresentationMode === 'picture-in-picture') {
      video.webkitSetPresentationMode('inline');
    }
  }

  /* ======================================================================
     Carousel
     ====================================================================== */

  function Carousel(root) {
    this.root = root;
    this.track = root.querySelector('[data-svc-track]');
    this.prev = root.querySelector('[data-svc-prev]');
    this.next = root.querySelector('[data-svc-next]');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('[data-svc-slide]'));
    this.states = new Map();
    this.controller = new AbortController();
    this.frame = 0;
    this.suspended = false; // true while the popup is open: card videos stay paused
    this.popup = null;
    this.videoSlides = [];
    if (this.track) this.init();
  }

  Carousel.prototype.init = function () {
    var self = this;
    var signal = this.controller.signal;

    this.track.addEventListener('scroll', function () { self.scheduleArrows(); }, { passive: true, signal: signal });
    if (this.prev) this.prev.addEventListener('click', function () { self.page(-1); }, { signal: signal });
    if (this.next) this.next.addEventListener('click', function () { self.page(1); }, { signal: signal });

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(function () { self.scheduleArrows(); });
      this.resizeObserver.observe(this.track);
    }

    var dialog = this.root.querySelector('[data-svc-modal]');
    if (dialog && typeof dialog.showModal === 'function') {
      this.popup = new Popup(this, dialog);
      this.videoSlides = this.slides.filter(function (slide) { return !!slide.querySelector('[data-svc-open]'); });
    }

    this.slides.forEach(function (slide) { self.setupSlide(slide); });

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var state = self.states.get(entry.target);
          if (!state) return;
          state.visible = entry.isIntersecting && entry.intersectionRatio >= 0.6;
          self.apply(state);
        });
      }, { threshold: [0, 0.6] });
      this.slides.forEach(function (slide) { self.observer.observe(slide); });
    } else {
      this.states.forEach(function (state) { state.visible = true; self.apply(state); });
    }

    this.updateArrows();
  };

  /* ---------- Arrows ---------- */

  Carousel.prototype.scheduleArrows = function () {
    var self = this;
    if (this.frame) return;
    this.frame = window.requestAnimationFrame(function () {
      self.frame = 0;
      self.updateArrows();
    });
  };

  Carousel.prototype.updateArrows = function () {
    if (!this.prev && !this.next) return;
    var max = this.track.scrollWidth - this.track.clientWidth;
    var overflow = max > 2;
    var left = this.track.scrollLeft;
    if (this.prev) {
      this.prev.hidden = !overflow;
      this.prev.disabled = left <= 2;
    }
    if (this.next) {
      this.next.hidden = !overflow;
      this.next.disabled = left >= max - 2;
    }
  };

  Carousel.prototype.step = function () {
    if (this.slides.length < 2) return this.track.clientWidth;
    return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
  };

  Carousel.prototype.page = function (direction) {
    var step = this.step();
    var perPage = Math.max(1, Math.floor((this.track.clientWidth + 1) / step));
    this.track.scrollBy({ left: direction * perPage * step, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  Carousel.prototype.goTo = function (blockId, instant) {
    var slide = this.slides.filter(function (el) { return el.getAttribute('data-block-id') === blockId; })[0];
    if (!slide || !this.slides[0]) return;
    this.track.scrollTo({
      left: slide.offsetLeft - this.slides[0].offsetLeft,
      behavior: instant || reduceMotion ? 'auto' : 'smooth'
    });
  };

  /* ---------- Slides & video ---------- */

  Carousel.prototype.setupSlide = function (slide) {
    var self = this;
    var signal = this.controller.signal;
    var state = {
      slide: slide,
      video: slide.querySelector('.svc__media video'),
      iframe: slide.querySelector('iframe[data-svc-src]'),
      toggle: slide.querySelector('[data-svc-toggle]'),
      pip: slide.querySelector('[data-svc-pip]'),
      visible: false,
      userPaused: reduceMotion,
      ytLoaded: false,
      ytReady: false
    };
    this.states.set(slide, state);

    if (state.video) {
      state.video.muted = true;
      state.video.defaultMuted = true;
      state.video.loop = true;
      state.video.playsInline = true;
      state.video.setAttribute('playsinline', '');
      state.video.addEventListener('leavepictureinpicture', function () { self.apply(state); }, { signal: signal });
      state.video.addEventListener('webkitpresentationmodechanged', function () { self.apply(state); }, { signal: signal });
    }

    if (state.toggle) {
      state.toggle.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        state.userPaused = !state.userPaused;
        self.apply(state);
      }, { signal: signal });
    }

    if (state.pip) this.setupPiP(state);

    var opener = slide.querySelector('[data-svc-open]');
    if (opener) {
      if (this.popup) {
        opener.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation(); // don't follow a full-card link
          self.popup.open(slide);
        }, { signal: signal });
      } else {
        opener.hidden = true; // browser without <dialog>: keep the card as it was
      }
    }

    this.setupProductPicker(slide, signal);
    this.renderToggle(state);
  };

  /* ---------- Several products per card (also used inside the popup) ---------- */

  Carousel.prototype.setupProductPicker = function (container, signal) {
    var picks = Array.prototype.slice.call(container.querySelectorAll('[data-svc-pick]'));
    var panels = Array.prototype.slice.call(container.querySelectorAll('[data-svc-panel]'));
    if (picks.length < 2) return;

    picks.forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation(); // don't trigger a full-card link
        var index = button.getAttribute('data-svc-pick');
        picks.forEach(function (other) {
          var active = other === button;
          other.classList.toggle('is-active', active);
          other.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        panels.forEach(function (panel) {
          panel.classList.toggle('is-active', panel.getAttribute('data-svc-panel') === index);
        });
      }, { signal: signal });
    });
  };

  Carousel.prototype.renderToggle = function (state) {
    state.slide.classList.toggle('is-paused', state.userPaused);
    if (state.toggle) state.toggle.setAttribute('aria-label', state.userPaused ? 'Play video' : 'Pause video');
  };

  Carousel.prototype.suspend = function (flag) {
    var self = this;
    this.suspended = flag;
    this.states.forEach(function (state) { self.apply(state); });
  };

  Carousel.prototype.apply = function (state) {
    var shouldPlay = !this.suspended && state.visible && !state.userPaused;
    this.renderToggle(state);

    if (state.video) {
      if (shouldPlay) {
        quietPlay(state.video);
      } else if (!inPiP(state.video)) {
        state.video.pause();
      }
    }

    if (state.iframe) {
      if (shouldPlay && !state.ytLoaded) {
        this.loadYouTube(state); // embed URL autoplays
      } else {
        this.youtube(state, shouldPlay ? 'playVideo' : 'pauseVideo');
      }
    }
  };

  Carousel.prototype.loadYouTube = function (state) {
    var self = this;
    var iframe = state.iframe;
    state.ytLoaded = true;
    iframe.addEventListener('load', function () {
      iframe.classList.add('is-loaded');
      state.ytReady = true;
      // State may have changed while the player loaded
      window.setTimeout(function () {
        self.youtube(state, !self.suspended && state.visible && !state.userPaused ? 'playVideo' : 'pauseVideo');
      }, 400);
    }, { once: true, signal: this.controller.signal });
    iframe.src = iframe.getAttribute('data-svc-src');
  };

  Carousel.prototype.youtube = function (state, command) {
    if (!state.ytReady || !state.iframe.contentWindow) return;
    state.iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: command, args: [] }), YT_ORIGIN);
  };

  /* ---------- Picture-in-picture on the card (uploaded videos) ---------- */

  Carousel.prototype.setupPiP = function (state) {
    var self = this;
    var video = state.video;
    var button = state.pip;

    if (!pipSupported(video)) {
      button.hidden = true;
      return;
    }
    button.hidden = false;

    function enter() {
      state.userPaused = false;
      self.apply(state);
      requestPiP(video);
    }

    button.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();

      if (inPiP(video)) {
        exitPiP(video);
        return;
      }

      if (video.readyState >= 1) {
        enter();
      } else {
        video.preload = 'metadata';
        video.addEventListener('loadedmetadata', enter, { once: true, signal: self.controller.signal });
        video.load();
      }
    }, { signal: this.controller.signal });
  };

  /* ---------- Teardown ---------- */

  Carousel.prototype.destroy = function () {
    if (this.popup) this.popup.destroy();
    this.controller.abort();
    if (this.observer) this.observer.disconnect();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.frame) window.cancelAnimationFrame(this.frame);
    this.states.forEach(function (state) {
      if (state.video && !inPiP(state.video)) state.video.pause();
    });
    this.states.clear();
  };

  /* ======================================================================
     Popup with sound
     ====================================================================== */

  function Popup(carousel, dialog) {
    this.carousel = carousel;
    this.dialog = dialog;
    this.stage = dialog.querySelector('[data-svc-modal-stage]');
    this.mediaBox = dialog.querySelector('[data-svc-modal-media]');
    this.products = dialog.querySelector('[data-svc-modal-products]');
    this.count = dialog.querySelector('[data-svc-modal-count]');
    this.closeBtn = dialog.querySelector('[data-svc-modal-close]');
    this.prevBtn = dialog.querySelector('[data-svc-modal-prev]');
    this.nextBtn = dialog.querySelector('[data-svc-modal-next]');
    this.muteBtn = dialog.querySelector('[data-svc-modal-mute]');
    this.toggleBtn = dialog.querySelector('[data-svc-modal-toggle]');
    this.pipBtn = dialog.querySelector('[data-svc-modal-pip]');
    this.bar = dialog.querySelector('[data-svc-modal-progress]');
    this.barFill = this.bar ? this.bar.querySelector('span') : null;
    this.startWithSound = dialog.getAttribute('data-sound') === 'true';
    this.index = -1;
    this.active = false; // popup open and not yet cleaned up
    this.media = null;   // { type: 'video', el, card } or { type: 'youtube', el, muted, playing, ready }
    this.item = null;    // AbortController for the listeners of the video on screen
    this.opener = null;
    this.bind();
  }

  Popup.prototype.bind = function () {
    var self = this;
    var dialog = this.dialog;
    var signal = this.carousel.controller.signal;

    this.closeBtn.addEventListener('click', function () { self.close(); }, { signal: signal });

    // Esc: close through our own path so cleanup runs right away
    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      self.close();
    }, { signal: signal });

    // Backup for any other way the dialog gets closed. Browsers queue this
    // event (and can hold it in background tabs), so it isn't relied on —
    // and a late one is ignored if the popup has been opened again since.
    dialog.addEventListener('close', function () {
      if (!dialog.open) self.onClose();
    }, { signal: signal });

    // Click on the dimmed area around the popup closes it
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) self.close();
    }, { signal: signal });

    if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self.step(-1); }, { signal: signal });
    if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self.step(1); }, { signal: signal });

    document.addEventListener('keydown', function (event) {
      if (!dialog.open || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); self.step(-1); }
      else if (event.key === 'ArrowRight') { event.preventDefault(); self.step(1); }
    }, { signal: signal });

    // Swipe left/right on the video (phones)
    var startX = null;
    var startY = 0;
    this.stage.addEventListener('touchstart', function (event) {
      if (event.touches.length !== 1) { startX = null; return; }
      startX = event.touches[0].clientX;
      startY = event.touches[0].clientY;
    }, { passive: true, signal: signal });
    this.stage.addEventListener('touchend', function (event) {
      if (startX === null) return;
      var touch = event.changedTouches[0];
      var dx = touch.clientX - startX;
      var dy = touch.clientY - startY;
      startX = null;
      if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) self.step(dx < 0 ? 1 : -1);
    }, { signal: signal });

    this.toggleBtn.addEventListener('click', function () { self.togglePlay(); }, { signal: signal });
    this.muteBtn.addEventListener('click', function () { self.toggleMute(); }, { signal: signal });
    if (this.pipBtn) this.pipBtn.addEventListener('click', function () { self.togglePiP(); }, { signal: signal });

    if (this.bar) {
      this.bar.addEventListener('click', function (event) {
        var media = self.media;
        if (!media || media.type !== 'video' || !(media.el.duration > 0) || !isFinite(media.el.duration)) return;
        var rect = self.bar.getBoundingClientRect();
        var ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
        media.el.currentTime = ratio * media.el.duration;
      }, { signal: signal });
    }
  };

  Popup.prototype.open = function (slide) {
    var index = this.carousel.videoSlides.indexOf(slide);
    if (index < 0) return;
    this.opener = slide.querySelector('[data-svc-open]');
    this.active = true;
    this.carousel.suspend(true);
    document.documentElement.classList.add('svc-modal-open');
    if (!this.dialog.open) this.dialog.showModal();
    this.show(index); // same click, so the browser allows sound
  };

  Popup.prototype.close = function () {
    if (this.dialog.open) this.dialog.close();
    this.onClose();
  };

  Popup.prototype.onClose = function () {
    if (!this.active) return; // already cleaned up
    this.active = false;
    var slide = this.carousel.videoSlides[this.index];
    var media = this.media;

    // A video moved to picture-in-picture keeps playing after the popup closes;
    // it's cleaned up when its PiP window is closed.
    if (!(media && media.type === 'video' && inPiP(media.el))) this.clearMedia();

    this.products.innerHTML = '';
    document.documentElement.classList.remove('svc-modal-open');
    this.carousel.suspend(false);
    if (slide) this.carousel.goTo(slide.getAttribute('data-block-id'), true);
    if (this.opener && this.opener.isConnected) {
      try { this.opener.focus({ preventScroll: true }); } catch (error) { /* ignore */ }
    }
  };

  Popup.prototype.step = function (direction) {
    var index = this.index + direction;
    if (index < 0 || index >= this.carousel.videoSlides.length) return;
    this.show(index);
  };

  Popup.prototype.show = function (index) {
    var self = this;
    var slides = this.carousel.videoSlides;
    var slide = slides[index];
    if (!slide) return;

    this.clearMedia();
    this.index = index;
    this.item = new AbortController();
    var signal = this.item.signal;
    var muted = popupMuted === null ? !this.startWithSound : popupMuted;

    // Uploaded videos show at their own shape; YouTube uses the section's ratio
    var ratio = slide.getAttribute('data-svc-ratio');
    if (ratio) this.stage.style.setProperty('--svc-modal-ratio', ratio);
    else this.stage.style.removeProperty('--svc-modal-ratio');

    var cardVideo = slide.querySelector('.svc__media video');
    var cardFrame = slide.querySelector('.svc__media iframe[data-svc-src]');

    if (cardVideo) {
      var video = cardVideo.cloneNode(true);
      video.className = 'svc-modal__video';
      video.removeAttribute('id');
      video.preload = 'auto';
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.defaultMuted = muted;
      video.muted = muted;
      this.mediaBox.appendChild(video);
      if (cardVideo.currentTime > 0) {
        try { video.currentTime = cardVideo.currentTime; } catch (error) { /* ignore */ }
      }

      var media = { type: 'video', el: video, card: cardVideo };
      this.media = media;

      ['play', 'pause', 'volumechange', 'enterpictureinpicture'].forEach(function (name) {
        video.addEventListener(name, function () { self.render(); }, { signal: signal });
      });
      video.addEventListener('timeupdate', function () { self.renderProgress(); }, { signal: signal });
      video.addEventListener('leavepictureinpicture', function () {
        if (!self.dialog.open && self.media === media) self.clearMedia();
        else self.render();
      }, { signal: signal });
      video.addEventListener('webkitpresentationmodechanged', function () {
        if (!self.dialog.open && self.media === media && !inPiP(video)) self.clearMedia();
        else self.render();
      }, { signal: signal });

      var attempt = video.play();
      if (attempt && attempt.catch) {
        attempt.catch(function (error) {
          // Browser refused sound: play muted; the mute button turns it on.
          if (self.media !== media || !error || error.name !== 'NotAllowedError') return;
          video.muted = true;
          quietPlay(video);
          self.render();
        });
      }
    } else if (cardFrame) {
      var poster = slide.querySelector('.svc__poster');
      if (poster) {
        var posterCopy = poster.cloneNode(true);
        posterCopy.className = 'svc-modal__poster';
        posterCopy.removeAttribute('loading');
        this.mediaBox.appendChild(posterCopy);
      }

      var frame = document.createElement('iframe');
      frame.className = 'svc-modal__iframe';
      frame.title = cardFrame.getAttribute('title') || 'Video';
      frame.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture; fullscreen');
      frame.setAttribute('tabindex', '-1');
      frame.src = cardFrame.getAttribute('data-svc-src').replace(/([?&])mute=1/, '$1mute=' + (muted ? '1' : '0'));

      var yt = { type: 'youtube', el: frame, muted: muted, playing: true, ready: false };
      this.media = yt;
      frame.addEventListener('load', function () {
        yt.ready = true;
        frame.classList.add('is-loaded');
        window.setTimeout(function () {
          if (self.media !== yt) return;
          self.youtube(yt.muted ? 'mute' : 'unMute');
          self.youtube(yt.playing ? 'playVideo' : 'pauseVideo');
        }, 400);
      }, { once: true, signal: signal });
      this.mediaBox.appendChild(frame);
    }

    // Products: same thumbnails and panels as the card, with the same product selected
    this.products.innerHTML = '';
    var thumbs = slide.querySelector('.svc__thumbs');
    var panels = slide.querySelector('.svc__panels');
    if (thumbs) this.products.appendChild(thumbs.cloneNode(true));
    if (panels) this.products.appendChild(panels.cloneNode(true));
    Array.prototype.forEach.call(this.products.querySelectorAll('.svc__stretch'), function (el) {
      el.classList.remove('svc__stretch');
    });
    var hasProducts = !!this.products.querySelector('.svc__title, .svc__price, .svc__button');
    this.carousel.setupProductPicker(this.products, signal);

    var title = this.products.querySelector('.svc__panel.is-active .svc__title');
    var label = 'Video ' + (index + 1) + ' of ' + slides.length;
    this.dialog.setAttribute('aria-label', title ? title.textContent.trim() + ' – ' + label : label);
    if (this.count) this.count.textContent = (index + 1) + ' / ' + slides.length;

    this.dialog.classList.toggle('is-video-only', !hasProducts);
    this.dialog.classList.toggle('is-single', slides.length < 2);
    this.dialog.classList.toggle('is-youtube', !!this.media && this.media.type === 'youtube');
    if (this.prevBtn) this.prevBtn.disabled = index === 0;
    if (this.nextBtn) this.nextBtn.disabled = index === slides.length - 1;
    if (this.pipBtn) this.pipBtn.hidden = !(this.media && this.media.type === 'video' && pipSupported(this.media.el));

    // A disabled button can't keep focus; keep keyboard users inside the popup
    var active = document.activeElement;
    if (!active || active.disabled || !this.dialog.contains(active)) this.closeBtn.focus();

    this.render();
    this.renderProgress();
  };

  Popup.prototype.clearMedia = function () {
    var media = this.media;
    this.media = null;
    if (this.item) {
      this.item.abort();
      this.item = null;
    }
    if (media && media.type === 'video') {
      var video = media.el;
      if (inPiP(video)) exitPiP(video);
      // Card picks up where the popup left off
      if (media.card && video.currentTime > 0) {
        try { media.card.currentTime = video.currentTime; } catch (error) { /* ignore */ }
      }
      video.pause();
      Array.prototype.forEach.call(video.querySelectorAll('source'), function (source) { source.remove(); });
      video.removeAttribute('src');
      try { video.load(); } catch (error) { /* ignore */ }
    }
    this.mediaBox.innerHTML = '';
  };

  Popup.prototype.isMuted = function () {
    var media = this.media;
    if (!media) return true;
    return media.type === 'video' ? media.el.muted : media.muted;
  };

  Popup.prototype.isPaused = function () {
    var media = this.media;
    if (!media) return true;
    return media.type === 'video' ? media.el.paused : !media.playing;
  };

  Popup.prototype.render = function () {
    var muted = this.isMuted();
    var paused = this.isPaused();
    this.dialog.classList.toggle('is-muted', muted);
    this.dialog.classList.toggle('is-paused', paused);
    this.muteBtn.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
    this.toggleBtn.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
    if (this.pipBtn) {
      var media = this.media;
      var active = !!media && media.type === 'video' && inPiP(media.el);
      this.pipBtn.setAttribute('aria-label', active ? 'Exit picture-in-picture' : 'Picture-in-picture');
    }
  };

  Popup.prototype.renderProgress = function () {
    if (!this.barFill) return;
    var media = this.media;
    var pct = 0;
    if (media && media.type === 'video' && media.el.duration > 0 && isFinite(media.el.duration)) {
      pct = (media.el.currentTime / media.el.duration) * 100;
    }
    this.barFill.style.width = pct + '%';
  };

  Popup.prototype.toggleMute = function () {
    var media = this.media;
    if (!media) return;
    var muted = !this.isMuted();
    popupMuted = muted;
    if (media.type === 'video') {
      media.el.muted = muted;
      if (!muted && media.el.paused) quietPlay(media.el);
    } else {
      media.muted = muted;
      this.youtube(muted ? 'mute' : 'unMute');
    }
    this.render();
  };

  Popup.prototype.togglePlay = function () {
    var media = this.media;
    if (!media) return;
    if (media.type === 'video') {
      if (media.el.paused) quietPlay(media.el);
      else media.el.pause();
    } else {
      media.playing = !media.playing;
      this.youtube(media.playing ? 'playVideo' : 'pauseVideo');
      this.render();
    }
  };

  Popup.prototype.togglePiP = function () {
    var media = this.media;
    if (!media || media.type !== 'video') return;
    var video = media.el;
    if (inPiP(video)) {
      exitPiP(video);
      return;
    }
    if (video.readyState >= 1) {
      requestPiP(video);
    } else {
      video.addEventListener('loadedmetadata', function () { requestPiP(video); }, { once: true, signal: this.item.signal });
    }
  };

  Popup.prototype.youtube = function (command) {
    var media = this.media;
    if (!media || media.type !== 'youtube' || !media.ready || !media.el.contentWindow) return;
    media.el.contentWindow.postMessage(JSON.stringify({ event: 'command', func: command, args: [] }), YT_ORIGIN);
  };

  Popup.prototype.destroy = function () {
    var wasActive = this.active || this.dialog.open;
    this.active = false;
    this.clearMedia();
    if (wasActive) {
      try { this.dialog.close(); } catch (error) { /* ignore */ }
      document.documentElement.classList.remove('svc-modal-open');
    }
  };

  /* ======================================================================
     Registry
     ====================================================================== */

  function create(root) {
    if (instances.has(root)) return;
    try {
      instances.set(root, new Carousel(root));
    } catch (error) {
      if (window.console) console.error('[shoppable-video] init failed:', error);
    }
  }

  function remove(root) {
    var instance = instances.get(root);
    if (!instance) return;
    instance.destroy();
    instances.delete(root);
  }

  function scan(scope) {
    instances.forEach(function (instance, root) {
      if (!root.isConnected) remove(root);
    });
    var container = scope || document;
    if (container.matches && container.matches('[data-svc]')) create(container);
    Array.prototype.forEach.call(container.querySelectorAll('[data-svc]'), create);
  }

  function rootsIn(target) {
    if (!target || !target.querySelectorAll) return [];
    return Array.prototype.slice.call(target.querySelectorAll('[data-svc]'));
  }

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    rootsIn(event.target).forEach(remove);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target && event.target.closest && event.target.closest('[data-svc]');
    var instance = root && instances.get(root);
    if (instance) instance.goTo(event.detail.blockId);
  });

  window.ShoppableVideoCarousel = { scan: scan, instances: instances };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } else {
    scan(document);
  }
})();
